import React, { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import styles from '../styles.module.scss';
import { GET_TEAM_CONTACTS_FOR_BINDING } from '../../../../api/placeholders/queries';
import { useContactPicker } from '../ContactPickerContext';
import { ReactComponent as ChevronLeftIcon } from '../../../../shared/icons/chevron-left.svg';

// Standard bindable fields
const BINDABLE_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'company', label: 'Company' },
  { key: 'title', label: 'Title' },
  { key: 'address', label: 'Address' },
];

const ContactPickerOverlay: React.FC = () => {
  const { isOpen, config, closeContactPicker } = useContactPicker();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [selectedField, setSelectedField] = useState<string>('');

  const { data: contactsData, loading } = useQuery(GET_TEAM_CONTACTS_FOR_BINDING, {
    variables: { teamId: config?.teamId, search: searchQuery },
    skip: !config?.teamId || !isOpen,
  });

  const contacts = contactsData?.teamContacts || [];

  const filteredContacts = useMemo(() => {
    if (!searchQuery) return contacts;
    const query = searchQuery.toLowerCase();
    return contacts.filter(
      (contact: any) =>
        contact.name?.toLowerCase().includes(query) ||
        contact.email?.toLowerCase().includes(query) ||
        contact.company?.toLowerCase().includes(query)
    );
  }, [contacts, searchQuery]);

  const handleConfirm = () => {
    if (selectedContact && selectedField && config?.onSelect) {
      config.onSelect(selectedContact.id, selectedField);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedContact(null);
    setSelectedField('');
    closeContactPicker();
  };

  const handleBack = () => {
    setSelectedContact(null);
    setSelectedField('');
  };

  // Get available fields for selected contact (only show fields with values)
  const availableFields = useMemo(() => {
    if (!selectedContact) return [];

    // Standard fields that have values
    const standardFields = BINDABLE_FIELDS.filter((field) => selectedContact[field.key]);

    // Custom fields that have values
    const customFieldsList: any[] = [];
    if (selectedContact.teamCustomFields && selectedContact.customFields) {
      selectedContact.teamCustomFields.forEach((schema: any) => {
        const fieldValue = selectedContact.customFields[schema.fieldKey];
        if (fieldValue) {
          customFieldsList.push({
            key: `custom.${schema.fieldKey}`,
            label: schema.fieldLabel,
            isCustom: true,
          });
        }
      });
    }

    // Combine with divider if there are custom fields
    if (customFieldsList.length > 0) {
      return [
        ...standardFields,
        { isDivider: true, key: 'divider', label: 'Custom Fields' },
        ...customFieldsList,
      ];
    }

    return standardFields;
  }, [selectedContact]);


  if (!isOpen) return null;

  return (
    <div className={styles.contactPickerOverlay}>
      {/* Header */}
      <div className={styles.overlayHeader}>
        <button
          className={styles.overlayBackButton}
          onClick={selectedContact ? handleBack : handleClose}
          aria-label="Back"
        >
          <ChevronLeftIcon className={styles.overlayBackIcon} />
        </button>
        <span className={styles.overlayTitle}>
          {selectedContact ? 'Select Field' : 'Select Contact'}
        </span>
        <button className={styles.closeButton} onClick={handleClose}>
          &times;
        </button>
      </div>

      {/* Content */}
      <div className={styles.overlayContent}>
        {!selectedContact ? (
          <>
            {/* Sticky search section */}
            <div className={styles.searchSection}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <div className={styles.searchDivider} />
            </div>

            {/* Scrollable results */}
            {loading ? (
              <div className={styles.loading}>
                <div className={styles.spinner} />
              </div>
            ) : (
              <div className={styles.contactsList}>
                {filteredContacts.map((contact: any) => (
                  <div
                    key={contact.id}
                    className={styles.contactItem}
                    onClick={() => setSelectedContact(contact)}
                  >
                    <div className={styles.contactName}>{contact.name}</div>
                    {contact.email && <div className={styles.contactEmail}>{contact.email}</div>}
                  </div>
                ))}

                {filteredContacts.length === 0 && (
                  <div className={styles.emptyState}>
                    <p>No contacts found</p>
                    <p className={styles.fieldsSubtitle}>
                      Add contacts in the People page
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className={styles.fieldSelectionWrapper}>
            {/* Sticky header section */}
            <div className={styles.fieldSelectionHeader}>
              {/* Combined context box showing placeholder and contact */}
              <div className={styles.linkingContextBox}>
                {config?.placeholderName && (
                  <div className={styles.linkingContextRow}>
                    <span className={styles.linkingContextLabel}>Placeholder</span>
                    <span className={styles.linkingContextValue}>{config.placeholderName}</span>
                  </div>
                )}
                <div className={styles.linkingContextRow}>
                  <span className={styles.linkingContextLabel}>Contact</span>
                  <span className={styles.linkingContextValue}>{selectedContact.name}</span>
                </div>
              </div>

              <p className={styles.fieldSelectionLabel}>Choose field to link:</p>
            </div>

            {/* Scrollable fields list */}
            <div className={styles.fieldsListScrollable}>
              {availableFields.map((field: any) => {
                // Render divider
                if (field.isDivider) {
                  return (
                    <div key={field.key} className={styles.fieldDivider}>
                      <hr style={{ margin: '0.5rem 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                      <span style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 500 }}>
                        {field.label}
                      </span>
                    </div>
                  );
                }

                // Get field value - custom fields are in customFields object
                const fieldValue = field.isCustom
                  ? selectedContact.customFields?.[field.key.replace('custom.', '')]
                  : selectedContact[field.key];

                return (
                  <div
                    key={field.key}
                    className={`${styles.fieldItem} ${selectedField === field.key ? styles.selected : ''}`}
                    onClick={() => setSelectedField(field.key)}
                  >
                    <span className={styles.fieldValue}>
                      {fieldValue || '(empty)'}
                    </span>
                    <span className={styles.fieldLabel}>{field.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Sticky footer with Apply button */}
            <div className={styles.fieldSelectionFooter}>
              <button
                className={styles.applyButton}
                onClick={handleConfirm}
                disabled={!selectedField}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactPickerOverlay;
